/**
 * ProjectProcurementTab
 * ---------------------
 * What was awarded and what it costs us. Shows the award that created the
 * project (solicitation or quote), every awarded trade with its committed
 * cost, and the full supplier payables ledger — POs issued, invoices received,
 * payments made and the balance still owed.
 */
import { Award, Building2, ExternalLink, FileText, Hammer } from "lucide-react";
import SupplierPayablesPanel from "./SupplierPayablesPanel";

const money = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Legacy seeded records stored their titles already HTML-escaped.
const txt = (v) => String(v ?? "").replace(/&amp;/g, "&").replace(/&#39;|&apos;/g, "'").replace(/&quot;/g, '"');

// Quote-converted projects carry the money on the divisions, not the partner.
const committedFor = (row, divisions) => {
  if (row.committed_cost != null) return money(row.committed_cost);
  if (row.amount != null) return money(row.amount);
  const company = String(row.company || row.subcontractor || "").toLowerCase().replace(/[.,]/g, "");
  if (!company) return "—";
  const hit = divisions.filter((d) => {
    const resp = String(d.responsible || d.subcontractor || "").toLowerCase().replace(/[.,]/g, "");
    return resp && (company.startsWith(resp) || resp.startsWith(company));
  });
  if (!hit.length) return "—";
  return money(hit.reduce((s, d) => s + (Number(d.amount) || 0), 0));
};

export const ProjectProcurementTab = ({ projectId, project, token }) => {
  const sol = project?.source_solicitation;
  const quote = project?.source_quote;
  const partners = project?.trade_partners || [];
  const divisions = project?.divisions_summary || [];

  return (
    <div className="space-y-6" data-testid="project-procurement-tab">
      {/* Award that created this project */}
      <div className="card-clinical p-4">
        <h3 className="text-sm font-black text-slate-900 inline-flex items-center gap-2 mb-3">
          <Award className="w-4 h-4 text-emerald-600" /> Award &amp; contract
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Fact label="Owner / agency" value={txt(project?.owner_name || project?.client_org_name || "—")} />
          <Fact label="Owner job / solicitation no." value={project?.estimate_number || project?.code || "—"} />
          <Fact label="Contract value" value={money(project?.revised_contract_value ?? project?.contract_value ?? project?.budget)} />
          <Fact label="Committed to subs / suppliers"
            value={project?.committed_subcontract_cost != null
              ? money(project.committed_subcontract_cost)
              : "See POs below"} testId="procurement-committed" />
        </div>
        {(sol || quote) && (
          <div className="mt-3 flex flex-wrap gap-2">
            {sol?.packet_url && (
              <a href={sol.packet_url} target="_blank" rel="noreferrer"
                className="text-xs font-semibold text-blue-700 hover:underline inline-flex items-center gap-1">
                <FileText className="w-3.5 h-3.5" /> Bid packet {sol.number ? `(${sol.number})` : ""}
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
            {quote?.pdf_url && (
              <a href={quote.pdf_url} target="_blank" rel="noreferrer"
                className="text-xs font-semibold text-blue-700 hover:underline inline-flex items-center gap-1">
                <FileText className="w-3.5 h-3.5" /> Source quote
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        )}
      </div>

      {/* Awarded trades */}
      {(partners.length > 0 || divisions.length > 0) && (
        <div className="card-clinical p-4">
          <h3 className="text-sm font-black text-slate-900 inline-flex items-center gap-2 mb-3">
            <Hammer className="w-4 h-4 text-blue-600" /> Awarded trades &amp; suppliers
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[620px] text-sm">
              <thead className="bg-slate-50">
                <tr>{["Trade / division", "Company", "Contact", "Committed cost"].map((h) => (
                  <th key={h} className="text-left px-3 py-2 text-[10px] font-black uppercase tracking-wider text-slate-500">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(partners.length ? partners : divisions).map((row, i) => (
                  <tr key={i} data-testid={`awarded-trade-${i}`}>
                    <td className="px-3 py-2 text-slate-900">{txt(row.trade || row.code || row.title || "—")}</td>
                    <td className="px-3 py-2 font-semibold text-slate-900">
                      <span className="inline-flex items-center gap-1.5">
                        <Building2 className="w-3.5 h-3.5 text-slate-400" />
                        {txt(row.company || row.subcontractor || "—")}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-600 text-xs">
                      {txt(row.contact_name || "—")}
                      {(row.portal_user_email || row.email) ? ` · ${row.portal_user_email || row.email}` : ""}
                    </td>
                    <td className="px-3 py-2 tabular-nums font-bold text-slate-900">
                      {committedFor(row, divisions)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Payables ledger */}
      <div className="card-clinical p-4">
        <h3 className="text-sm font-black text-slate-900 mb-3">Purchase orders, invoices &amp; payments</h3>
        <SupplierPayablesPanel linkType="project" linkId={projectId} token={token} />
      </div>
    </div>
  );
};

const Fact = ({ label, value, testId }) => (
  <div data-testid={testId}>
    <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</p>
    <p className="text-sm font-bold text-slate-900 break-words">{value}</p>
  </div>
);

export default ProjectProcurementTab;
